import React, { useState, useCallback, useEffect } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Users, ScrollText, Settings,
  LogOut, Menu, X, ChevronRight, Wallet,
} from 'lucide-react';

const NAV = [
  { to: '/admin',        label: 'Dashboard',  Icon: LayoutDashboard, exact: true },
  { to: '/admin/users',  label: 'Users',      Icon: Users },
  { to: '/admin/logs',   label: 'Audit Logs', Icon: ScrollText },
  { to: '/admin/settings', label: 'Settings', Icon: Settings },
];

const BRAND_BG  = 'linear-gradient(135deg,#c44569 0%,#cf6a87 100%)';
const PRIMARY   = '#c44569';
const PRIMARY_LT = '#fdf0f4';

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);


  const handleKey = useCallback((e) => { if (e.key === 'Escape') setSidebarOpen(false); }, []);
  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'A';

  const sidebarW = 260;

  return (
    <div className="min-h-screen max-h-screen flex" style={{ background: '#f8fafc' }}>

      {/* ── Overlay (mobile only) ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{ background: 'rgba(30,10,18,0.45)', backdropFilter: 'blur(2px)' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        className="fixed top-0 left-0 h-screen z-50 lg:static lg:z-auto flex flex-col transition-all duration-300 ems-card-elevated"
        style={{
          width: sidebarW,
          background: '#fff',
          boxShadow: '2px 0 18px rgba(15,23,42,0.09)',
          transform: sidebarOpen ? 'translateX(0)' : `translateX(-${sidebarW}px)`,
        }}
      >
        {/* Professional Logo Header */}
        <div className="flex items-center justify-between px-5 py-6" style={{ background: BRAND_BG }}>
          <div className="flex items-center gap-3 flex-1">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg" style={{ background:'rgba(255,255,255,0.25)',backdropFilter:'blur(10px)',border:'1px solid rgba(255,255,255,0.4)' }}>
              <Wallet size={20} color="#fff" strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <p className="text-white font-black text-base leading-tight tracking-tighter">EMS</p>
              <p className="text-white/80 text-xs font-medium">Admin Panel</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden w-8 h-8 rounded-lg flex items-center justify-center transition"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}
            onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.25)'}
            onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.15)'}
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        {/* Admin badge */}
        <div className="mx-4 mt-4 mb-2 rounded-2xl px-4 py-3 flex items-center gap-3" 
          style={{ background: '#fdf0f4', border: '1.5px solid #fce0e8' }}>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-xs shrink-0"
            style={{ background: BRAND_BG, color: '#fff' }}>
            {initials}
          </div>
          <div className="min-w-0">
            <p className="font-bold text-sm text-slate-800 truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
          <p className="text-xs font-semibold uppercase tracking-widest px-3 py-2"
            style={{ color: PRIMARY, opacity: 0.6 }}>Menu</p>
          {NAV.map(({ to, label, Icon, exact }) => (
            <NavLink
              key={to}
              to={to}
              end={exact}
              onClick={() => window.innerWidth < 1024 && setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all group ${
                  isActive ? 'text-white shadow-md' : 'text-slate-600 hover:text-slate-800'
                }`
              }
              style={({ isActive }) => isActive
                ? { background: BRAND_BG, boxShadow: '0 4px 12px rgba(196,69,105,0.25)' }
                : {}}
            >
              {({ isActive }) => (
                <>
                  <Icon size={16} strokeWidth={isActive ? 2.5 : 2.2} />
                  <span className="flex-1">{label}</span>
                  {isActive && <ChevronRight size={14} strokeWidth={2.5} />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <div className="px-3 pb-5">
          <div className="border-t mb-3" style={{ borderColor: '#fce0e8' }} />
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all"
            style={{ background: '#fee2e2', color: '#dc2626' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#fecaca'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fee2e2'; }}
          >
            <LogOut size={16} strokeWidth={2.2} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <div
        className="flex-1 flex flex-col h-screen transition-all duration-300"
        style={{ marginLeft: sidebarOpen || window.innerWidth < 1024 ? 0 : sidebarW }}
      >
        {/* Top bar */}
        <header
          className="sticky top-0 z-30 flex items-center justify-between px-5 h-14 shrink-0"
          style={{ background: '#fff', borderBottom: '1.5px solid #fce0e8', boxShadow: '0 1px 8px rgba(196,69,105,0.07)' }}
        >
          <button
            onClick={() => setSidebarOpen(s => !s)}
            className="flex items-center justify-center w-9 h-9 rounded-xl transition"
            style={{ background: PRIMARY_LT, color: PRIMARY }}
            onMouseEnter={e => e.currentTarget.style.background = '#fce0e8'}
            onMouseLeave={e => e.currentTarget.style.background = PRIMARY_LT}
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? <X size={19} strokeWidth={2.2} /> : <Menu size={19} strokeWidth={2.2} />}
          </button>

          {/* Right — brand */}
          <div className="flex items-center gap-2">
            <span className="text-base font-extrabold tracking-tight" style={{ color: PRIMARY }}>EMS</span>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center shadow" style={{ background: PRIMARY }}>
              <Wallet size={14} color="#fff" strokeWidth={2.5} />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-5 lg:p-7 overflow-auto bg-slate-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
